import { useLazyQuery, useQuery } from '@apollo/client';
import type {
  Cml_Cml,
  Programming_Assignment,
  Programming_SubmissionSchema,
} from '__generated__/graphql-types-do-not-use';

import ProgrammingAssignment from 'bundles/programming/api/ProgrammingAssignmentQuery.graphql';
import type {
  ProgrammingAssignmentQuery,
  ProgrammingAssignmentQueryVariables,
} from 'bundles/programming/api/__generated__/ProgrammingAssignmentQuery';

export type UseProgrammingAssignmentQueryParams = {
  courseId: string;
  itemId: string;
};

export type LearnerAssignment = {
  maxScore?: number;
  passingFraction?: number;
  passingScore?: number;
  partSchemas?: Programming_Assignment['partSchemas'];
  submissionSchema?: Programming_SubmissionSchema;
  versionedProgrammingAssignmentId?: string;
  assignmentInstructions?: Cml_Cml;
};

// This is used when the caller wants a response immediately
export const useProgrammingAssignmentQuery = (courseId: string, itemId: string, skipQuery: boolean = false) => {
  const result = useQuery<ProgrammingAssignmentQuery, ProgrammingAssignmentQueryVariables>(ProgrammingAssignment, {
    variables: {
      courseId,
      itemId,
    },
    context: { clientName: 'gatewayGql' },
    skip: skipQuery,
  });
  const { data, error, loading, refetch } = result;

  const assignment = data?.Programming_Assignment?.queryProgrammingAssignment ?? undefined;

  return {
    assignment,
    error,
    loading,
    refetch,
  };
};

// This is used when the caller wants to trigger the query manually
export const useLazyProgrammingAssignmentQuery = (courseId: string, itemId: string) => {
  const [loadProgrammingAssignment, { data, error, loading, refetch }] = useLazyQuery<
    ProgrammingAssignmentQuery,
    ProgrammingAssignmentQueryVariables
  >(ProgrammingAssignment, {
    variables: {
      courseId,
      itemId,
    },
    context: { clientName: 'gatewayGql' },
  });

  const assignment = data?.Programming_Assignment?.queryProgrammingAssignment ?? undefined;
  return [
    loadProgrammingAssignment,
    {
      assignment,
      error,
      loading,
      refetch,
    },
  ] as const;
};

export default useProgrammingAssignmentQuery;
